// Typed API client. Cloudflare Access injects the identity JWT at the edge, so the browser
// sends no credentials itself. SSE is consumed via a streaming fetch reader (EventSource
// can't POST). STOP = abort the run fetch (ORCHESTRATOR-DESIGN §4.4, §6).

import type { ImportResponse, ManagerScorecard, RunEvent, AggregateResult, MeddpiccReport, Coaching, UnscoredSkill } from './types.js';

export interface ReportCopyMeta {
  repName: string;
  repRole: string;
  businessName: string;
  callType: string;
}

// ---- Access-session-expiry guard --------------------------------------------------------------
// When a Cloudflare Access session expires, the edge answers a same-origin /api request with a 302
// to the cross-origin Access login. A normal fetch() FOLLOWS that redirect and the browser then
// blocks reading the cross-origin response → the call rejects with an opaque "Failed to fetch",
// which surfaces to the user as a cryptic error (and, on the apex domain, the same thing happens
// because /api 301s to www — a cross-origin hop). Using `redirect: 'manual'` turns that redirect
// into a detectable `opaqueredirect` response (status 0) instead of a followed cross-origin request,
// so we can react deliberately: a FULL-PAGE reload re-triggers the Access login (document navigation
// is NOT CORS-blocked the way fetch is), and after signing in the user lands back in the app.

export class AuthExpiredError extends Error {
  constructor() {
    super('Your session expired. Reloading to sign you back in.');
    this.name = 'AuthExpiredError';
  }
}

/** What to do when an /api call is redirected to the login. The app sets this (notice + reload); the
 *  default is a bare reload so the guard still works if the handler was never wired. */
let authExpiredHandler: () => void = () => window.location.reload();
export function setAuthExpiredHandler(fn: () => void): void {
  authExpiredHandler = fn;
}

// ---- Clerk bearer token (dormant unless the app shell wires a provider) ------------------------
// By default the browser sends no auth header — Cloudflare Access injects the identity JWT at the
// edge. When Clerk runs in the app shell it registers a provider here that returns a FRESH session
// token per call (Clerk's getToken() refreshes as needed), which apiFetch attaches as
// `Authorization: Bearer …`. This is what keeps an authed session from going stale mid-use. Returns
// null → no header → the request falls through to the Access path (so dual-auth still works).
let authTokenProvider: (() => Promise<string | null>) | null = null;
export function setAuthTokenProvider(fn: () => Promise<string | null>): void {
  authTokenProvider = fn;
}
async function authHeader(): Promise<Record<string, string>> {
  if (!authTokenProvider) return {};
  try {
    const token = await authTokenProvider();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

// DEMO MODE: a per-device toggle that adds X-Demo:1 to every /api call. The staging worker then serves a
// pre-seeded demo tenant; prod ignores the header (no DEMO_TENANT_ID). Turned on by opening the app with
// ?demo=1 and off with ?demo=0 — call initDemoFlag() once at boot to sync from the URL.
const DEMO_KEY = 'studio.demo.v1';
function demoOn(): boolean {
  try { return localStorage.getItem(DEMO_KEY) === '1'; } catch { return false; }
}
/** Read ?demo=1 / ?demo=0 from the URL once at startup and persist it. Returns the current state. */
export function initDemoFlag(): boolean {
  try {
    const v = new URLSearchParams(location.search).get('demo');
    if (v === '1') localStorage.setItem(DEMO_KEY, '1');
    else if (v === '0') localStorage.removeItem(DEMO_KEY);
  } catch { /* storage / URL unavailable */ }
  return demoOn();
}
function demoHeader(): Record<string, string> {
  return demoOn() ? { 'X-Demo': '1' } : {};
}
/** Current demo-mode state (for the staging demo toggle button). */
export function isDemoOn(): boolean {
  return demoOn();
}
/** Turn demo mode on/off (persisted). The caller reloads so all data re-fetches under the new tenant. */
export function setDemoMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(DEMO_KEY, '1');
    else localStorage.removeItem(DEMO_KEY);
  } catch { /* storage unavailable */ }
}

/**
 * fetch() for our own /api. `redirect: 'manual'` makes an Access-login redirect show up as an
 * `opaqueredirect` (status 0) instead of a followed cross-origin request; we treat that as
 * "session expired" → run the handler (reload to re-login) and throw AuthExpiredError so callers
 * stop. A genuine network failure still throws the underlying TypeError, untouched.
 */
async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const auth = await authHeader();
  const res = await fetch(input, {
    redirect: 'manual',
    ...init,
    headers: { ...(init.headers as Record<string, string> | undefined), ...auth, ...demoHeader() },
  });
  if (res.type === 'opaqueredirect' || res.status === 0) {
    authExpiredHandler();
    throw new AuthExpiredError();
  }
  return res;
}

/** Best-effort: emails a copy of the downloaded report to Project Moneyball. Never throws fatally.
 *  Deliberately uses raw fetch (not apiFetch): it fires after a successful download/print, so a stale
 *  session here must NOT yank the user into a reload — the report they wanted already rendered. */
export async function sendReportCopy(result: AggregateResult, meta: ReportCopyMeta): Promise<void> {
  await fetch('/api/report-copy', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ result, meta }),
  }).catch(() => undefined);
}

/** Email the current report (Studio Moneyball brand) to the signed-in user, or to `to` if provided.
 *  Uses apiFetch so the Clerk bearer is attached (auth is required — it's a real user action). */
export async function emailReport(
  result: AggregateResult,
  meta: ReportCopyMeta,
  to?: string,
): Promise<{ ok: boolean; to?: string }> {
  const res = await apiFetch('/api/report-email', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(to ? { to, result, meta } : { result, meta }),
  });
  return jsonOrThrow<{ ok: boolean; to?: string }>(res);
}

/** An API failure that carries the server's error CODE + HTTP status, so callers can branch on the
 *  specific failure (e.g. the 402 TRIAL_EXHAUSTED / BILLING_REQUIRED gate) instead of only its message. */
export class ApiError extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string; code?: string } } | null;
    throw new ApiError(body?.error?.message ?? `Request failed (${res.status})`, body?.error?.code, res.status);
  }
  return (await res.json()) as T;
}

export async function importTranscript(transcript: string, signal?: AbortSignal, anonymize = false): Promise<ImportResponse> {
  const res = await apiFetch('/api/import', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, ...(anonymize ? { anonymize: true } : {}) }),
    signal,
  });
  return jsonOrThrow<ImportResponse>(res);
}

export interface TrendContext {
  repName?: string;
  callType?: string;
  stage?: string;
  /** Trend-gate flags from import (echoed so the server can exclude synthetic, non-in-app calls). */
  synthetic?: boolean;
  anonymizedInApp?: boolean;
  /** "Add this call to Benchmark" — default true. When false the scored call is stored but kept out of
   *  every analytics read (Trends / Benchmark / Average Scores). */
  includeInBenchmark?: boolean;
  /** The call's actual date/time (epoch ms), set by the user on the Run panel. Overrides the server-side
   *  transcript parse. Omit to let the server parse it (or mark the call "Not Available"). */
  callTimeMs?: number | null;
}

/** Run scoring; yields RunEvents as they stream. Pass a signal to STOP. `trend` carries the
 *  import-classified rep/call context, used only for the optional scores-only trend record. */
export async function* runScoring(
  sessionId: string,
  transcript: string,
  scorecard: ManagerScorecard,
  signal: AbortSignal,
  fresh = false,
  trend?: TrendContext,
): AsyncGenerator<RunEvent> {
  const res = await apiFetch('/api/run', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      transcript,
      scorecard,
      fresh,
      rep_name: trend?.repName ?? '',
      call_type: trend?.callType ?? null,
      stage: trend?.stage ?? null,
      synthetic: trend?.synthetic ?? false,
      anonymized_in_app: trend?.anonymizedInApp ?? false,
      include_in_benchmark: trend?.includeInBenchmark !== false,
      ...(typeof trend?.callTimeMs === 'number' ? { call_time_ms: trend.callTimeMs } : {}),
    }),
    signal,
  });
  if (!res.ok || !res.body) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Run failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (line) {
        try {
          yield JSON.parse(line.slice(5).trim()) as RunEvent;
        } catch {
          /* ignore keep-alive / partial */
        }
      }
    }
  }
}

/** Buyer-Side Diagnostic run; yields RunEvents (buyer_phase milestones + buyer_side_ready) as they
 *  stream. Standalone mode — no scorecard. Pass a signal to STOP; deal_label labels a cohort point. */
export async function* runBuyerSide(
  transcript: string,
  signal: AbortSignal,
  fresh = false,
  dealLabel?: string,
): AsyncGenerator<RunEvent> {
  const res = await apiFetch('/api/buyer-side', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, fresh, ...(dealLabel ? { deal_label: dealLabel } : {}) }),
    signal,
  });
  if (!res.ok || !res.body) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Analysis failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (line) {
        try {
          yield JSON.parse(line.slice(5).trim()) as RunEvent;
        } catch {
          /* ignore keep-alive / partial */
        }
      }
    }
  }
}

/** Evidence-grounded coaching summary for a scored call (auxiliary, non-scoring). Reads the cached
 *  skill scores server-side + one cached model call; returns {} until the call has been scored, or on
 *  any failure — the caller just doesn't render the section. */
export async function getCoaching(
  transcript: string,
  scorecard: ManagerScorecard,
  callType?: string | null,
  repName?: string | null,
): Promise<{ coaching?: Coaching; unscored?: UnscoredSkill[] }> {
  const res = await apiFetch('/api/coaching', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, scorecard, call_type: callType ?? '', rep_name: repName ?? '' }),
  });
  return jsonOrThrow(res);
}

/** Token-free live re-score: recomputes the aggregate for changed weights/expected from cached
 * skill scores. Returns the new result, or {needs_scoring} if some skill must be scored first. */
export async function reaggregate(
  transcript: string,
  scorecard: ManagerScorecard,
): Promise<{ result?: AggregateResult; needs_scoring?: boolean; meddpicc?: MeddpiccReport }> {
  const res = await apiFetch('/api/aggregate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, scorecard }),
  });
  return jsonOrThrow(res);
}

/** Toggle whether this scored call feeds Trends / Benchmark / Average Scores. Matched server-side by the
 *  transcript's one-way hash. Best-effort — resolves even if the call wasn't persisted (trends off). */
export async function setBenchmarkInclusion(transcript: string, include: boolean): Promise<void> {
  await apiFetch('/api/benchmark-inclusion', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, include }),
  });
}

/** Set the actual date/time (epoch ms) for an already-scored call whose date was "Not Available".
 *  Matched server-side by the transcript's one-way hash; moves the call onto the trend axis and clears
 *  its `undated` flag. Best-effort — resolves even if the call wasn't persisted (trends off). */
export async function setCallDate(transcript: string, callTimeMs: number): Promise<void> {
  await apiFetch('/api/call-date', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ transcript, call_time_ms: callTimeMs }),
  });
}

// ---- settings + trends ----
export interface TrendPoint {
  ts: number;
  call_type: string | null;
  final_score: number;
  scores: Record<string, number | null>;
}

export async function getSettings(): Promise<{ trends_opt_in: boolean; trends_available: boolean; auto_save_pdf: boolean; auto_save_available: boolean; report_recipients?: string[]; report_cc_enabled?: boolean }> {
  return jsonOrThrow(await apiFetch('/api/settings'));
}

/** Save the report CC recipients (up to 5) and whether the EMAIL button also sends to them. */
export async function setReportRecipients(
  recipients: string[],
  enabled: boolean,
): Promise<{ ok: boolean; report_recipients?: string[]; report_cc_enabled?: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/settings', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ report_recipients: recipients, report_cc_enabled: enabled }),
    }),
  );
}

/** Flip the tenant's auto-save-reports setting (Phase 3). */
export async function setAutoSavePdf(on: boolean): Promise<{ ok: boolean; auto_save_pdf?: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/settings', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ auto_save_pdf: on }) }),
  );
}

// ---- report archive (Phase 3) ----
export interface SavedReport {
  report_id: string;
  rep_name: string | null;
  call_type: string | null;
  business: string | null;
  final_score: number | null;
  ts: number;
}
/** Best-effort: archive a downloaded report's data (server enforces the auto_save_pdf flag). */
export async function saveReport(payload: unknown): Promise<{ ok: boolean; report_id?: string }> {
  return jsonOrThrow(await apiFetch('/api/reports', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) }));
}
export async function listReports(): Promise<{ reports: SavedReport[] }> {
  return jsonOrThrow(await apiFetch('/api/reports'));
}
export async function getReport(id: string): Promise<{ result?: unknown; meta?: unknown; buyer?: unknown; meddpicc?: unknown }> {
  return jsonOrThrow(await apiFetch(`/api/reports/${encodeURIComponent(id)}`));
}

/** The caller's identity: tenant, effective role, and permission set. Drives role-based nav-hiding. */
export interface Me {
  email: string;
  tenant_id: string;
  role: 'admin' | 'super_user' | 'member';
  permissions: string[];
  /** The view this user lands on at login (a top-level view id). Defaults to 'home'. */
  homepage: string;
}
export async function getMe(): Promise<Me> {
  return jsonOrThrow<Me>(await apiFetch('/api/me'));
}

/** Manual upgrade-request FALLBACK (backoffice): flags the account so it surfaces there and billing is
 *  arranged personally. Self-serve purchase is `startCheckout` below; this stays as a fallback.
 *  NOTE: currently has no caller in the client (kept for the backoffice fallback path). `alreadyActive`
 *  = nothing to request. */
export async function requestUpgrade(): Promise<{ ok: boolean; requested: boolean; alreadyActive: boolean }> {
  return jsonOrThrow(await apiFetch('/api/request-upgrade', { method: 'POST' }));
}

/** Mint a Lemon Squeezy hosted checkout for a product variant and return its URL to redirect to.
 *  Throws (jsonOrThrow) on error — notably a 503 while checkout is not yet configured. */
export async function startCheckout(variantId: string): Promise<{ ok: boolean; url: string }> {
  return jsonOrThrow(
    await apiFetch('/api/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ variantId }),
    }),
  );
}

export interface BillingSummary {
  configured: boolean;
  plan: string | null;
  status: string | null;
  nextRenewal: string | null; // ISO
  portalUrl: string | null;
  invoices: { date: string; item: string; amount: string; url: string | null }[];
  callsLeft: number;
}
/** Plan + next renewal + invoices + Lemon customer-portal URL + call balance for the Billing page. */
export async function getBillingSummary(): Promise<BillingSummary> {
  return jsonOrThrow<BillingSummary>(await apiFetch('/api/billing-summary'));
}

// ---- self-service Account (Phase 2) ----
export interface AccountMember {
  email: string;
  role: string;
  status: string;
  name: string | null;
  calls: number;
  /** The view this user lands on at login (a top-level view id). Defaults to 'home'. */
  homepage: string;
}
export interface AccountSummary {
  tenantId: string;
  displayName: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  planTier: string;
  billingStatus: string;
  billingEmail: string | null;
  phoneCc: string | null;
  phone: string | null;
  trialActive: boolean;
  trialDaysLeft: number;
  granted: number;
  callsTotal: number;
  freeUsed: number;
  creditsUsed: number;
  creditsLeft: number;
  members: AccountMember[];
  /** True when self-service invites are wired (Cloudflare Access is configured); false keeps the
   *  "coming soon" note and hides the invite form. */
  invitesEnabled?: boolean;
}
/** The caller's own account (details, plan, credits, usage, users). Tenant-scoped server-side. */
export async function getAccount(): Promise<AccountSummary> {
  return jsonOrThrow<AccountSummary>(await apiFetch('/api/account'));
}

/** Edit own account details (company name / billing email). Tenant-scoped server-side. */
export async function updateAccount(patch: { display_name?: string; billing_email?: string; phone_cc?: string; phone?: string }): Promise<{ ok: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/account', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) }),
  );
}

/** Manage an existing member of your account: change role, or pause/resume. Server enforces the
 *  self-lockout guards (owner/self/admin protected) and tenant scope. */
export async function setAccountMember(
  email: string,
  action: 'set_role' | 'pause' | 'resume' | 'set_homepage',
  role?: 'owner' | 'member',
  homepage?: string,
): Promise<{ ok: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/account/members', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, action, ...(role ? { role } : {}), ...(homepage ? { homepage } : {}) }),
    }),
  );
}

/** Invite a new teammate into your account. Creates their membership and adds them to the login
 *  allowlist. Server enforces one-email-one-account, super-user gating, and tenant scope. */
export async function inviteMember(email: string, role: 'owner' | 'member', homepage?: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch('/api/account/invite', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, role, ...(homepage ? { homepage } : {}) }),
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as { ok: boolean; error?: string };
}

/** Remove a teammate from your account (revokes their login access). Owner/self/admin are protected. */
export async function removeMember(email: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch('/api/account/remove', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as { ok: boolean; error?: string };
}

export async function setTrendsOptIn(on: boolean): Promise<{ trends_opt_in: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/settings', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ trends_opt_in: on }) }),
  );
}

/** The rep's last ≤5 same-call-type points (oldest→newest). Empty when trends are off/unavailable. */
export async function getTrends(repName: string, callType?: string | null): Promise<TrendPoint[]> {
  const q = new URLSearchParams({ rep_name: repName });
  if (callType) q.set('call_type', callType);
  const res = await apiFetch(`/api/trends?${q.toString()}`);
  const body = (await jsonOrThrow<{ calls: TrendPoint[] }>(res)) ?? { calls: [] };
  return body.calls ?? [];
}

// ---- Team Trends (Phase 3) ----
export interface TeamRepTrend {
  repKey: string;
  name: string;
  confidence: string;
  teamId: string | null;
  teamName: string | null;
  regionId: string | null;
  regionName: string | null;
  managerId: string | null;
  managerName: string | null;
  classId: string | null;
  className: string | null;
  hidden: boolean;
  callCount: number;
  latest: number | null;
  first: number | null;
  avg: number | null;
  lastTs: number | null;
  points: { ts: number; final_score: number }[];
}

// ---- rep teams (Phase 2) ----
export interface Team {
  team_id: string;
  name: string;
  created_at: number;
  rep_count: number;
}
export async function listTeams(): Promise<{ teams: Team[] }> {
  return jsonOrThrow(await apiFetch('/api/teams'));
}
export async function createTeam(name: string): Promise<{ ok: boolean; error?: string; team_id?: string }> {
  return jsonOrThrow(await apiFetch('/api/teams', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'create', name }) }));
}
export async function renameTeam(team_id: string, name: string): Promise<{ ok: boolean; error?: string }> {
  return jsonOrThrow(await apiFetch('/api/teams', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'rename', team_id, name }) }));
}
export async function deleteTeam(team_id: string): Promise<{ ok: boolean }> {
  return jsonOrThrow(await apiFetch('/api/teams', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'delete', team_id }) }));
}
export async function assignRepTeam(rep_key: string, team_id: string | null): Promise<{ ok: boolean; error?: string }> {
  return jsonOrThrow(await apiFetch('/api/teams/assign', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rep_key, team_id }) }));
}

// ---- Trends page (Phase 3): filters + overview (graphs + heatmap) ----
export interface TrendsFilters {
  trendsOff?: boolean;
  groups: { key: string; count: number }[];
  teams: { id: string; name: string; rep_count: number }[];
  regions: { id: string; name: string; rep_count: number }[];
  managers: { id: string; name: string; rep_count: number }[];
  classes: { id: string; name: string; rep_count: number }[];
  reps?: { key: string; name: string; count: number }[]; // Rep's-name filter options (rep_key + display name)
}
export interface TrendBucket { key: string; startMs: number; label: string }
export interface TrendSkillSeries {
  skill_id: string;
  points: { bucket: string; avg: number | null; bar: number | null; n: number }[];
}
export interface TrendHeatCell { bucket: string; avg: number | null; bar: number | null; n: number; status: 'above' | 'near' | 'below' | 'na' }
export type TrendConfidence = 'high' | 'medium' | 'low';
export interface TrendSummaryStat { skill_id: string; kind: 'strength' | 'gap'; pct: number; n: number; confidence: TrendConfidence; critical: boolean }
export interface TrendMover { skill_id: string; from: number; to: number; delta: number }
export interface TrendsSummary {
  strengths: TrendSummaryStat[];
  gaps: TrendSummaryStat[];
  finalDirection: { direction: 'up' | 'down' | 'flat'; from: number | null; to: number | null };
  improved: TrendMover[];
  declined: TrendMover[];
}
export interface TrendsOverview {
  trendsOff?: boolean;
  error?: string;
  group: string;
  granularity: 'day' | 'week' | 'month';
  buckets: TrendBucket[];
  heatmapBuckets: TrendBucket[];
  finalScore: { bucket: string; avg: number | null; n: number }[];
  skills: TrendSkillSeries[];
  heatmap: { skill_id: string; cells: TrendHeatCell[] }[];
  summary: TrendsSummary;
  meta: { totalCalls: number; callsWithBar: number; fromMs: number; toMs: number };
}
export interface TrendsSelection {
  window?: string; // '7d'|'30d'|'90d'|'6m'|'12m'|'custom'
  from?: string; // custom range start, YYYY-MM-DD (only sent when window==='custom')
  to?: string; // custom range end, YYYY-MM-DD (inclusive)
  granularity?: 'day' | 'week' | 'month'; // client-chosen to fit the viewport (overrides the server default)
  heatCols?: number; // client's width-derived heatmap column budget
  group?: string;
  team?: string | null; // '' = Unassigned; null/undefined = no filter
  region?: string | null;
  manager?: string | null;
  class?: string | null;
  rep?: string;
}
export async function getTrendsFilters(): Promise<TrendsFilters> {
  return jsonOrThrow(await apiFetch('/api/trends/filters'));
}
/** The scored rep's CLASS, resolved server-side via the real rep_key (robust to display-name edits). */
export async function getRepClass(name: string): Promise<{ className: string | null }> {
  return jsonOrThrow(await apiFetch(`/api/rep-class?name=${encodeURIComponent(name)}`));
}
export async function getTrendsOverview(sel: TrendsSelection): Promise<TrendsOverview> {
  const q = new URLSearchParams();
  if (sel.window) q.set('window', sel.window);
  if (sel.window === 'custom') {
    if (sel.from) q.set('from', sel.from);
    if (sel.to) q.set('to', sel.to);
  }
  if (sel.granularity) q.set('granularity', sel.granularity);
  if (sel.heatCols) q.set('heatCols', String(sel.heatCols));
  if (sel.group) q.set('group', sel.group);
  // A slice present as '' means the Unassigned bucket → send the empty value; undefined/absent = skip.
  for (const k of ['team', 'region', 'manager', 'class'] as const) {
    const v = sel[k];
    if (v !== undefined && v !== null) q.set(k, v);
  }
  if (sel.rep) q.set('rep', sel.rep);
  return jsonOrThrow(await apiFetch(`/api/trends/overview?${q.toString()}`));
}

// ---- rep regions & managers (Trends Phase 2) — same shape as teams, one generic client per kind ----
export interface OrgEntity {
  id: string;
  name: string;
  created_at: number;
  rep_count: number;
}
type OrgKind = 'regions' | 'managers' | 'classes';

export async function listOrgEntities(kind: OrgKind): Promise<OrgEntity[]> {
  const r = await jsonOrThrow<Record<string, OrgEntity[]>>(await apiFetch(`/api/${kind}`));
  return r[kind] ?? [];
}
export async function createOrgEntity(kind: OrgKind, name: string): Promise<{ ok: boolean; error?: string; id?: string }> {
  return jsonOrThrow(await apiFetch(`/api/${kind}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'create', name }) }));
}
export async function renameOrgEntity(kind: OrgKind, id: string, name: string): Promise<{ ok: boolean; error?: string }> {
  return jsonOrThrow(await apiFetch(`/api/${kind}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'rename', id, name }) }));
}
export async function deleteOrgEntity(kind: OrgKind, id: string): Promise<{ ok: boolean }> {
  return jsonOrThrow(await apiFetch(`/api/${kind}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action: 'delete', id }) }));
}
export async function assignRepOrgEntity(kind: OrgKind, rep_key: string, id: string | null): Promise<{ ok: boolean; error?: string }> {
  return jsonOrThrow(await apiFetch(`/api/${kind}/assign`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rep_key, id }) }));
}
/** Every rep's final-score series over ~24 months (manager overview). trendsOff = trends disabled.
 *  includeHidden also returns reps removed from the view (Show removed). */
export async function getTeamTrends(includeHidden = false): Promise<{ reps: TeamRepTrend[]; trendsOff?: boolean }> {
  return jsonOrThrow(await apiFetch(`/api/team-trends${includeHidden ? '?hidden=1' : ''}`));
}

/** Remove (hidden=true) or restore (false) reps in Team Trends. Reversible; trend data untouched. */
export async function hideReps(rep_keys: string[], hidden: boolean): Promise<{ ok: boolean; changed: number }> {
  return jsonOrThrow(
    await apiFetch('/api/reps/hide', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rep_keys, hidden }) }),
  );
}

/** Rename a rep (superuser edit on the Teams page). rep_key is unchanged, so trend history stays. */
export async function renameRep(rep_key: string, display_name: string): Promise<{ ok: boolean }> {
  return jsonOrThrow(
    await apiFetch('/api/reps/rename', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rep_key, display_name }) }),
  );
}

// ---- Compare (Phase 4) ----
// A comparison is between reps, or between values of one org entity (team/region/manager/class).
export type CompareMode = 'reps' | 'teams' | 'regions' | 'managers' | 'classes';
/** One compared column: its kind + the id within that kind (rep_key / team_id / region_id / …). */
export interface CompareColumn { kind: CompareMode; ref: string }
export interface CompareSide {
  id: string;
  kind?: CompareMode;
  name: string;
  count: number;
  final: number | null;
  scores: { key: string; label: string; avg: number | null }[];
}
export const MAX_COMPARE = 5;
export type CompareData =
  | { empty: true }
  | { columns: CompareColumn[]; sides: CompareSide[] };
export async function getCompare(window?: string): Promise<CompareData> {
  const q = window && window !== 'all' ? `?window=${encodeURIComponent(window)}` : '';
  return jsonOrThrow(await apiFetch(`/api/compare${q}`));
}
/** Set the active comparison from up to 5 columns, each its own kind ({ kind, ref }). */
export async function setCompare(columns: CompareColumn[]): Promise<{ ok: boolean; error?: string }> {
  return jsonOrThrow(
    await apiFetch('/api/compare', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ columns }) }),
  );
}
export async function clearCompare(): Promise<{ ok: boolean }> {
  return jsonOrThrow(await apiFetch('/api/compare/clear', { method: 'POST' }));
}

// ---- Secret Sauce (automatic Top-vs-Core differentiator engine) ----
export interface SecretSauceGap {
  skillId: string;
  skillName: string;
  topAvg: number;
  topN: number;
  coreAvg: number;
  coreN: number;
  gap: number;
}
export interface CoachableRep {
  name: string;
  avgFinal: number; // 0–100
  calls: number;
}
export interface SecretSauceCluster {
  group: string; // scorecard key, e.g. 'ProductDemo'
  topCalls: number;
  coreCalls: number;
  topFinal: number; // avg final score (0–100) for Top reps in this cluster
  coreFinal: number; // avg final score for Core reps
  gaps: SecretSauceGap[];
  coachable: CoachableRep[]; // Core reps furthest behind here, named (≤3)
  // Always-on coaching playbook (persisted server-side): the stored write, when it was written (epoch
  // ms), and whether the benchmark changed since (→ a "Regenerate" prompt). Absent/null = none stored yet.
  playbook?: PlaybookSynthesis | null;
  playbookWrittenAt?: number | null;
  playbookStale?: boolean;
}
export interface SecretSauceResponse {
  clusters?: SecretSauceCluster[];
  minN?: number;
  topAssigned?: number;
  coreAssigned?: number;
  topCalls?: number;
  coreCalls?: number;
  window?: { fromMs: number; toMs: number };
  needsClassification?: boolean;
  reason?: 'missing' | 'unassigned';
  trendsOff?: boolean;
  unavailable?: boolean;
}
/** The automatic Top-vs-Core, per-call-type-cluster differentiator table (no uploads). */
export async function getSecretSauce(): Promise<SecretSauceResponse> {
  return jsonOrThrow<SecretSauceResponse>(await apiFetch('/api/secret-sauce'));
}

// ---- Cohort Diagnostic synthesis ----
export interface PlaybookGapInput {
  skillName: string;
  topAvg: number | null;
  coreAvg: number | null;
  gap: number | null;
}
export interface PlaybookSynthesis {
  intro: string;
  focus: { skill: string; whatTopDo: string; likelyMissing: string; actions: string[] }[];
  playbookGaps: string[];
}

/** Turn the cohort gap table into a written coaching playbook (one model call, derived data only). When
 *  `group` (the cluster key) is passed, the server persists the result so the page is always-on; the
 *  returned `writtenAt` (epoch ms) is when it was stored. */
export async function synthesizePlaybook(
  callType: string,
  gaps: PlaybookGapInput[],
  group?: string,
): Promise<{ synthesis: PlaybookSynthesis; writtenAt: number | null }> {
  const res = await apiFetch('/api/playbook', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ callType, gaps, group }),
  });
  const body = await jsonOrThrow<{ ok: boolean; synthesis?: PlaybookSynthesis; writtenAt?: number | null }>(res);
  if (!body.ok || !body.synthesis) throw new Error('synthesis unavailable');
  return { synthesis: body.synthesis, writtenAt: body.writtenAt ?? null };
}

// ---- template CRUD ----
export async function listSystemTemplates(): Promise<ManagerScorecard[]> {
  return jsonOrThrow(await apiFetch('/api/templates/system'));
}
export async function listMyTemplates(): Promise<{ id: string; template_name: string; updated_at: string }[]> {
  return jsonOrThrow(await apiFetch('/api/templates'));
}
export async function getTemplate(id: string): Promise<ManagerScorecard> {
  return jsonOrThrow(await apiFetch(`/api/templates/${encodeURIComponent(id)}`));
}
export async function saveTemplate(sc: ManagerScorecard): Promise<{ id: string }> {
  return jsonOrThrow(
    await apiFetch('/api/templates', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(sc) }),
  );
}
// PUT replaces the whole scorecard (the worker validates the full body), so callers fetch the
// template first and resend it with the edited name — that's how rename is implemented.
export async function updateTemplate(id: string, sc: ManagerScorecard): Promise<{ id: string }> {
  return jsonOrThrow(
    await apiFetch(`/api/templates/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(sc),
    }),
  );
}
export async function deleteTemplate(id: string): Promise<{ ok: boolean }> {
  return jsonOrThrow(await apiFetch(`/api/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}
